import { Injectable } from '@angular/core';
import { ICarItem } from '@modules/car-booking/models/hotel.model';
import { CarDetail } from '@modules/car-booking/models/view-details';

type BadgeMapping = Record<string, any>;
type BadgeInfoResult = {
  badge_info_list: any[] | null;
  rank_info: any | null;
};

@Injectable({
  providedIn: 'root',
})
export class BadgeInfoService {
  constructor() {}

  getDistinctBadgeInfoList(badgeMapping: BadgeMapping): any[] | null {
    if (!badgeMapping) return null;

    return Object.values(badgeMapping)
      .map((item: any) => item?.badge_info)
      .filter((badge, index, self) => self.findIndex((b) => b?.id === badge?.id) === index);
  }

  getMaxKeyByReduceAmount(badgeMapping: BadgeMapping): 'HOURS' | 'KM' {
    return (['HOURS', 'KM'] as const).reduce((max, curr) => {
      const maxAmount = badgeMapping[max]?.rank_info?.reduce_amount || 0;
      const currAmount = badgeMapping[curr]?.rank_info?.reduce_amount || 0;
      return currAmount > maxAmount ? curr : max;
    }, 'HOURS');
  }

  getNormalizedReduceAmount(amount: number): number {
    if (amount < 2) {
      return amount > 0.1 ? 10 : 100;
    }
    return 1;
  }

  mappingBadgeInfo(
    badgeMapping: BadgeMapping,
    userCurrency: string,
    item: ICarItem
  ): BadgeInfoResult {
    if (!badgeMapping) {
      return {
        badge_info_list: null,
        rank_info: null,
      };
    }

    const badgeInfoList = this.getDistinctBadgeInfoList(badgeMapping);
    const maxKey = this.getMaxKeyByReduceAmount(badgeMapping);
    const maxRank = { ...badgeMapping[maxKey]?.rank_info };

    if (!maxRank) {
      return {
        badge_info_list: badgeInfoList,
        rank_info: null,
      };
    }

    const normalizedReduceAmount = this.getNormalizedReduceAmount(maxRank.reduce_amount);

    if (userCurrency === item?.currency) {
      let price = 0;
      let priceCanal = 0;

      if (maxKey === 'HOURS') {
        price = item?.price_per_hour || 0;
        priceCanal = item?.price_per_hour_canal || 0;
      } else {
        price = item?.price_per_distance || 0;
        priceCanal = item?.price_per_distance_canal || 0;
      }

      const ratio = maxRank.reduce_amount / price;

      maxRank.reduce_amount =
        this.getReduceAmountPrice(
          maxRank.reduce_amount,
          ratio,
          userCurrency,
          item?.currency,
          price,
          priceCanal
        ) * normalizedReduceAmount;
    } else {
      maxRank.reduce_amount *= normalizedReduceAmount;
    }

    return {
      badge_info_list: badgeInfoList,
      rank_info: maxRank,
    };
  }

  getMaxBadgeDetailInfo(badgeMapping: BadgeMapping): BadgeInfoResult {
    if (!badgeMapping) {
      return {
        badge_info_list: null,
        rank_info: null,
      };
    }

    const badgeInfoList = this.getDistinctBadgeInfoList(badgeMapping);
    const maxKey = this.getMaxKeyByReduceAmount(badgeMapping);
    const maxRank = { ...badgeMapping[maxKey]?.rank_info };

    if (!maxRank) {
      return {
        badge_info_list: badgeInfoList,
        rank_info: null,
      };
    }

    const normalizedReduceAmount = this.getNormalizedReduceAmount(maxRank.reduce_amount);
    maxRank.reduce_amount *= normalizedReduceAmount;

    return {
      badge_info_list: badgeInfoList,
      rank_info: maxRank,
    };
  }

  getReduceAmountPrice(
    reduceAmount: number,
    ratio: number,
    userCurrency: string,
    itemCurrency: string,
    itemPrice: number,
    itemPriceCanal: number
  ): number {
    if (userCurrency === itemCurrency) {
      if (itemPrice === 0) return 0;
      const roundedRatio = parseFloat(ratio.toFixed(2));
      return itemPriceCanal * roundedRatio;
    } else {
      return reduceAmount;
    }
  }
}
